import type { ConvexReactClient } from 'convex/react';
import { readBrowserClientSurface } from '@/lib/client-surface';
import type { NavigationDraftCheckpoint } from './navigation-draft-checkpoint';
import type { Id } from '@/convex/_generated/dataModel';
import { getFunctionName } from 'convex/server';
import { api } from '@/lib/convex';
import { withUserBlockingFailureReport } from '@/lib/observability/user-blocking-failure-reporter';
import type {
  OrgChatAttachmentInput,
  OrgChatPresetDataInput,
} from '@/components/org-chat/org-chat-types';
import type { OrgChatSendOptions } from '@/contexts/org-chat-interface-context';
import {
  dispatchThreadTargets,
  isSendAccepted,
} from '@/components/org-chat/org-chat-delivery';
import {
  getBrowserNavigationStorage,
  rememberPendingNavigationPrompt,
} from '@/components/chat/tool-calls/workspace-navigation-pending';
import { getConnectorQueueFields } from '@/components/org-chat/connector-queue-fields';
import { prepareSharedDispatchUploads } from '@/components/org-chat/org-chat-shared-dispatch-uploads';
import { buildBaseMessagePayload } from '@/components/org-chat/payload/build-base-payload';
import {
  buildAttachmentFields,
  buildPresetFields,
  mergeReferencePayloadFields,
  type OrgChatSendPayload,
} from '@/components/org-chat/org-chat-payload-builders';
import {
  designClosedArtifactPanelState,
  normalizeDesignTargetThreadRouteRefs,
} from './shared';
import {
  markClientMutationAck,
  markClientMutationDispatch,
} from '@/components/org-chat/client-visible-response-start';
import {
  hasPendingUploadSendTarget,
  rejectPendingUploadSendTarget,
  resolvePendingUploadSendTarget,
} from './pending-upload-send-targets';

/**
 * A send that took files along can no longer be created: no upload on this
 * device backs them (they were cancelled, or the page reloaded before the
 * send existed). They have to be attached again.
 */
export const PENDING_UPLOAD_FILES_GONE = 'PENDING_UPLOAD_FILES_GONE';

export type DesignNavigationDelivery = {
  id: string;
  actorId: string;
  orgSlug: string;
  routeRef: string;
  orgId: Id<'organizations'>;
  title: string;
  visibility: 'organization' | 'private';
  projectId?: Id<'threadFolders'>;
  prompt: string;
  rawUserPrompt?: string;
  attachments?: OrgChatAttachmentInput[];
  presetData?: OrgChatPresetDataInput;
  options: OrgChatSendOptions;
  sharedFields?: Partial<OrgChatSendPayload>;
  sourceDraftCheckpoint?: NavigationDraftCheckpoint;
  /**
   * Files still uploading that the first message takes along (#4844). The
   * task is created now with the message held; the server sends it once
   * every file is in.
   */
  pendingUploadIds?: string[];
};

/**
 * The files a failed send took along while they uploaded (#4844). By the
 * time it shows as failed their uploads have stopped, so these need
 * attaching again; a send that did get created is confirmed by the
 * reconciler instead.
 */
export function navigationFilesToReattach(
  job: Pick<DesignNavigationDelivery, 'attachments' | 'pendingUploadIds'>
): string[] {
  const ids = new Set(job.pendingUploadIds ?? []);
  return (job.attachments ?? []).flatMap((attachment) =>
    attachment.uploadId && ids.has(attachment.uploadId)
      ? [attachment.filename]
      : []
  );
}

/** The unbound snapshot survives navigation; each attempt binds each destination once. */
export async function deliverDesignNavigation(
  job: DesignNavigationDelivery,
  client: Pick<ConvexReactClient, 'query' | 'mutation'>,
  checkpoint: (job: DesignNavigationDelivery) => void,
  assertActor: () => void = () => {}
) {
  assertActor();
  // Older persisted navigation jobs may predate the input timestamp.
  if (job.options.clientSendStartedAt === undefined) {
    job.options = { ...job.options, clientSendStartedAt: Date.now() };
    checkpoint(job);
  }
  const clientSendStartedAt = job.options.clientSendStartedAt;
  const pendingUploadIds = job.pendingUploadIds ?? [];
  const attachmentFields = buildAttachmentFields(
    job.attachments,
    pendingUploadIds
  );
  const rememberAcceptedPrompt = (
    routeRef: string,
    agentThreadId: string,
    result: unknown
  ) => {
    if (
      routeRef !== job.routeRef ||
      !isSendAccepted(result) ||
      !result ||
      typeof result !== 'object' ||
      !('messageId' in result) ||
      typeof result.messageId !== 'string'
    )
      return;
    const storage = getBrowserNavigationStorage();
    if (storage)
      rememberPendingNavigationPrompt(storage, agentThreadId, result.messageId);
  };
  if (!job.sharedFields) {
    try {
      job.sharedFields = await prepareSharedDispatchUploads(
        { ...attachmentFields, ...buildPresetFields(job.presetData) },
        (uploads) => {
          assertActor();
          return client.mutation(
            api.generations.mutations.upsertComposerUploadedFilesForCurrentUser,
            { orgSlug: job.orgSlug, uploads }
          );
        },
        pendingUploadIds
      );
    } catch (error) {
      // No waiting send will exist for the files taken along: stop them.
      rejectPendingUploadSendTarget(job.id, error);
      throw error;
    }
    checkpoint(job);
  }
  const receipt = await dispatchThreadTargets({
    existingRouteRefs: normalizeDesignTargetThreadRouteRefs(job.options),
    newRouteRefs: job.options.newThreadRouteRefs ?? [],
    options: job.options,
    resolve: async (routeRefs) => {
      assertActor();
      if (!routeRefs.length) return [];
      const entries: Array<{
        routeRef: string;
        thread: { _id: Id<'orgThreads'>; threadId: string } | null;
      }> = await client.query(
        api.organizations.threads.queries.getManyByRouteRefsForCurrentUser,
        { routeRefs }
      );
      return entries.flatMap((entry) =>
        entry.thread
          ? [
              {
                routeRef: entry.routeRef,
                threadId: {
                  orgThreadId: entry.thread._id,
                  agentThreadId: entry.thread.threadId,
                },
              },
            ]
          : []
      );
    },
    createAndSend: async (routeId) => {
      assertActor();
      const clientInputId = `${job.id}:${routeId}`;
      markClientMutationDispatch(clientInputId, performance.now());
      if (pendingUploadIds.length)
        return createWithPendingUploads(routeId, clientInputId);
      const createAndSendMutation =
        api.organizations.threads.create_and_send.createThreadAndSendMessage;
      const receipt = await withUserBlockingFailureReport(
        {
          surface: 'create_thread',
          operation: getFunctionName(createAndSendMutation),
        },
        () =>
          client.mutation(createAndSendMutation, {
            ...buildMessage(routeId),
            routeId,
            title: job.title,
            visibility: job.visibility,
            ...(job.projectId ? { projectId: job.projectId } : {}),
          })
      );
      markClientMutationAck(clientInputId, performance.now());
      rememberAcceptedPrompt(routeId, receipt.threadId, receipt.result);
      return receipt.result;
    },
    send: async ({ orgThreadId, agentThreadId }, routeRef) => {
      assertActor();
      const clientInputId = `${job.id}:${routeRef}`;
      markClientMutationDispatch(clientInputId, performance.now());
      const sendMutation =
        api.products.ai.mutations.org_thread_send_message.sendMessage;
      const result = await withUserBlockingFailureReport(
        {
          surface: 'send',
          operation: getFunctionName(sendMutation),
          threadId: String(orgThreadId),
        },
        () =>
          client.mutation(sendMutation, {
            ...buildMessage(routeRef),
            orgThreadId,
          })
      );
      markClientMutationAck(clientInputId, performance.now());
      rememberAcceptedPrompt(routeRef, agentThreadId, result);
      return result;
    },
  });
  // Whatever stopped the send (actor change included), its files stop too.
  if (pendingUploadIds.length && !receipt.accepted)
    rejectPendingUploadSendTarget(
      job.id,
      new Error('PENDING_UPLOAD_SEND_NOT_ACCEPTED')
    );
  return receipt;

  /**
   * Creates the task with its message held for the files still uploading,
   * then binds those uploads to the waiting send. Null is the accepted
   * receipt of a deferred send.
   */
  async function createWithPendingUploads(
    routeId: string,
    clientInputId: string
  ) {
    // A retry, or a job restored after a reload, has no live uploads to
    // bind. Creating would leave file rows no device ever completes; only
    // a send that already exists is confirmed.
    if (!hasPendingUploadSendTarget(job.id)) {
      const held: string[] = await client.query(
        api.organizations.threads.send_receipts
          .listAcceptedSendInputsForCurrentUser,
        { inputs: [{ routeRef: routeId, clientInputId }] }
      );
      if (held.includes(clientInputId)) return null;
      throw new Error(PENDING_UPLOAD_FILES_GONE);
    }
    try {
      const createMutation =
        api.organizations.threads.pending_upload_sends
          .createThreadWithPendingUploads;
      const created = await withUserBlockingFailureReport(
        {
          surface: 'create_thread',
          operation: getFunctionName(createMutation),
        },
        () =>
          client.mutation(createMutation, {
            ...buildMessage(routeId),
            routeId,
            title: job.title,
            visibility: job.visibility,
            ...(job.projectId ? { projectId: job.projectId } : {}),
            pendingUploadIds,
          })
      );
      markClientMutationAck(clientInputId, performance.now());
      resolvePendingUploadSendTarget(job.id, {
        pendingSendId: created.pendingSendId,
        orgThreadId: created.orgThreadId,
        // The task's URL id, so the pill knows its page and links to it.
        routeRef: routeId,
      });
      return null;
    } catch (error) {
      rejectPendingUploadSendTarget(job.id, error);
      throw error;
    }
  }

  function buildMessage(routeRef: string) {
    return mergeReferencePayloadFields(
      {
        ...buildBaseMessagePayload({
          orgId: job.orgId,
          prompt: job.prompt,
          rawUserPrompt: job.rawUserPrompt,
          forceDeepResearch: job.options.forceDeepResearch,
        }),
        ...job.sharedFields,
        // Persisted failed jobs can contain old catalog-only selection fields.
        ...getConnectorQueueFields(job.sharedFields?.selectedConnectors),
        artifactPanelState: designClosedArtifactPanelState(),
        // Every branch (send, create, held create) reports the sender's screen.
        clientSurface: readBrowserClientSurface(),
        deliveryMetadata: {
          deliveryKind: 'send' as const,
          clientInputId: `${job.id}:${routeRef}`,
          createdAt: clientSendStartedAt,
        },
      },
      attachmentFields
    );
  }
}
